import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Location, TitleCasePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { TituloRotinaComponent } from '../shared/components/redesign/titulo-rotina/titulo-rotina.component';
import { SearchComponent } from '../shared/components/redesign/search/search.component';
import { ButtonComponent } from '../shared/components/redesign/botao/botao.component';
import { TabelaComponent } from '../shared/components/redesign/tabela/tabela.component';
import { TabelaAcaoComponent } from '../shared/components/redesign/tabela-acao/tabela-acao.component';
import { PaginadorComponent } from '../shared/components/redesign/paginador/paginador.component';
import { FiltrosTabComponent, FiltroTabItem } from '../shared/components/redesign/filtros-tab/filtros-tab.component';
import { PageEvent } from '@angular/material/paginator';
import { LembreteService } from '../shared/service/lembrete.service';
import { Lembrete } from '../shared/model/lembrete/lembrete.model';
import { FiltroLembrete } from '../shared/model/lembrete/filtro-lembrete.model';
import { LembreteContagem } from '../shared/model/lembrete/lembrete-contagem.model';
import { STATUS_LEMBRETE_ORDINAL, STATUS_LEMBRETE_LABEL, StatusLembreteEnum } from '../shared/enum/status-lembrete.enum';
import { TIPO_LEMBRETE_LABEL, TipoLembreteEnum } from '../shared/enum/tipo-lembrete.enum';
import { ModalCadastroLembreteComponent } from './modal-cadastro-lembrete/modal-cadastro-lembrete.component';
import { MenuMobileComponent } from '../shared/components/redesign/menu-mobile/menu-mobile.component';
import { CelularMaskPipe } from '../shared/pipe/celular-mask.pipe';
import { SocketService } from '../shared/service/socket.service';
import { LembreteAtualizacaoSocket } from '../shared/model/socket/lembrete-atualizacao-socket.model';
import { AvatarPessoaComponent } from '../shared/components/redesign/avatar-pessoa/avatar-pessoa.component';
import { AberturaContatoService } from '../shared/service/abertura-contato.service';

type AbaLembrete = 'TODOS' | 'PENDENTES' | 'CONCLUIDOS' | 'ERROS';

@Component({
  selector: 'app-lembrete',
  standalone: true,
  imports: [
    MatTableModule,
    MatTooltip,
    MatIcon,
    DatePipe,
    TituloRotinaComponent,
    SearchComponent,
    ButtonComponent,
    TabelaComponent,
    TabelaAcaoComponent,
    PaginadorComponent,
    FiltrosTabComponent,
    CelularMaskPipe,
    TitleCasePipe,
    AvatarPessoaComponent
  ],
  templateUrl: './lembrete.component.html',
  styleUrl: './lembrete.component.css'
})
export class LembreteComponent implements OnInit, OnDestroy {
  lembretes: Lembrete[] = [];
  dataSource = new MatTableDataSource<Lembrete>();
  carregando = false;
  carregandoMais = false;

  displayedColumns: string[] = ['gutterInicio', 'acao', 'lembrete', 'contato', 'tipo', 'dataHora', 'criadoPor', 'status', 'gutterFim'];

  page = 0;
  limit = 10;
  length = 0;
  pageSizeOptions = [10, 25, 50, 100];

  abaAtiva: AbaLembrete = 'TODOS';
  contagem: LembreteContagem = new LembreteContagem();
  carregandoContagem = true;
  filtro: FiltroLembrete = new FiltroLembrete();
  searchSubject = new Subject<string>();

  readonly tipoLabel = TIPO_LEMBRETE_LABEL;
  readonly statusLabel = STATUS_LEMBRETE_LABEL;
  readonly StatusLembreteEnum = StatusLembreteEnum;
  readonly TipoLembreteEnum = TipoLembreteEnum;

  private readonly lembreteService = inject(LembreteService);
  private readonly dialog = inject(MatDialog);
  private readonly location = inject(Location);
  private readonly aberturaContatoService = inject(AberturaContatoService);
  private readonly socketService = inject(SocketService);
  private lembreteSubscription?: Subscription;

  voltar() {
    this.location.back();
  }

  irParaContato(lembrete: Lembrete, evento?: Event) {
    evento?.stopPropagation();
    if (!lembrete.contatoNumero) {
      return;
    }
    this.aberturaContatoService.abrir(lembrete.contatoNumero);
  }

  ngOnInit() {
    this.configurarDebounce();
    this.buscarLembretes();
    this.buscarContagem();
    this.lembreteSubscription = this.socketService.escutarAtualizacaoLembrete()
      .subscribe(atualizacao => this.aplicarAtualizacaoLembrete(atualizacao));
  }

  ngOnDestroy() {
    this.lembreteSubscription?.unsubscribe();
  }

  private aplicarAtualizacaoLembrete(atualizacao: LembreteAtualizacaoSocket) {
    const indice = this.lembretes.findIndex(l => l.id === atualizacao.id);

    if (indice !== -1) {
      const filtroPorStatusAtivo = this.filtro.status !== undefined;
      const statusNovoOrdinal = STATUS_LEMBRETE_ORDINAL[atualizacao.statusNovo as StatusLembreteEnum];
      const aindaPertenceAoFiltro = !filtroPorStatusAtivo || statusNovoOrdinal === this.filtro.status;

      if (!aindaPertenceAoFiltro) {
        this.lembretes.splice(indice, 1);
        this.length = Math.max(0, this.length - 1);
      } else {
        const lembrete = this.lembretes[indice];
        lembrete.status = atualizacao.statusNovo as StatusLembreteEnum;
        lembrete.mensagemErro = atualizacao.mensagemErro;
        lembrete.dataConclusao = atualizacao.dataConclusao ? new Date(atualizacao.dataConclusao) : lembrete.dataConclusao;
        const acionavel = lembrete.status === StatusLembreteEnum.PENDENTE || lembrete.status === StatusLembreteEnum.ERRO;
        lembrete.podeEditar = acionavel;
        lembrete.podeProcessar = acionavel;
        lembrete.podeExcluir = acionavel;
      }

      this.lembretes = [...this.lembretes];
      this.dataSource = new MatTableDataSource(this.lembretes);
    }

    this.ajustarContagem(atualizacao.statusAnterior, atualizacao.statusNovo);
  }

  private ajustarContagem(statusAnterior: string, statusNovo: string) {
    this.incrementarBucketContagem(statusAnterior, -1);
    this.incrementarBucketContagem(statusNovo, 1);
  }

  private incrementarBucketContagem(status: string, delta: number) {
    const chave = this.bucketDoStatus(status);
    if (!chave) {
      return;
    }
    this.contagem[chave] = Math.max(0, this.contagem[chave] + delta);
  }

  private bucketDoStatus(status: string): 'pendentes' | 'concluidos' | 'erros' | null {
    switch (status) {
      case StatusLembreteEnum.PENDENTE: return 'pendentes';
      case StatusLembreteEnum.CONCLUIDO: return 'concluidos';
      case StatusLembreteEnum.ERRO: return 'erros';
      default: return null;
    }
  }

  buscarLembretes(anexar = false) {
    this.carregando = !anexar;
    this.carregandoMais = anexar;
    this.lembreteService.buscarPaginado(this.filtro, this.page, this.limit).subscribe({
      next: res => {
        const novosLembretes = res.content as Lembrete[];
        this.lembretes = anexar ? [...this.lembretes, ...novosLembretes] : novosLembretes;
        this.dataSource = new MatTableDataSource(this.lembretes);
        this.length = res.totalElements;
      },
      error: () => {
        this.carregando = false;
        this.carregandoMais = false;
      },
      complete: () => {
        this.carregando = false;
        this.carregandoMais = false;
      }
    });
  }

  onScrollListaMobile(event: Event) {
    const elemento = event.target as HTMLElement;
    const proximoDoFim = elemento.scrollTop + elemento.clientHeight >= elemento.scrollHeight - 150;
    if (proximoDoFim) {
      this.carregarMaisLembretesMobile();
    }
  }

  carregarMaisLembretesMobile() {
    if (this.carregando || this.carregandoMais || this.lembretes.length >= this.length) {
      return;
    }
    this.page++;
    this.buscarLembretes(true);
  }

  buscarContagem() {
    this.lembreteService.contarPorStatus().subscribe({
      next: res => this.contagem = res,
      complete: () => this.carregandoContagem = false
    });
  }

  get abasFiltro(): FiltroTabItem[] {
    return [
      { valor: 'TODOS', label: 'Todos', contador: this.contagem.todos },
      { valor: 'PENDENTES', label: 'Pendentes', contador: this.contagem.pendentes, corBolinha: '#ff9d33' },
      { valor: 'CONCLUIDOS', label: 'Concluídos', contador: this.contagem.concluidos, corBolinha: 'var(--verde-padrao)' },
      { valor: 'ERROS', label: 'Erro', contador: this.contagem.erros, corBolinha: 'var(--vermelho-padrao)' },
    ];
  }

  handlePageEvent(e: PageEvent) {
    this.page = e.pageIndex;
    this.limit = e.pageSize;
    this.buscarLembretes();
  }

  filtrarBusca(valor: string) {
    this.searchSubject.next(valor);
  }

  configurarDebounce(): void {
    this.searchSubject
      .pipe(debounceTime(300))
      .subscribe(valor => {
        this.filtro.busca = valor;
        this.zerarPaginacao();
      });
  }

  selecionarAba(aba: AbaLembrete) {
    this.abaAtiva = aba;
    switch (aba) {
      case 'PENDENTES':
        this.filtro.status = STATUS_LEMBRETE_ORDINAL[StatusLembreteEnum.PENDENTE];
        break;
      case 'CONCLUIDOS':
        this.filtro.status = STATUS_LEMBRETE_ORDINAL[StatusLembreteEnum.CONCLUIDO];
        break;
      case 'ERROS':
        this.filtro.status = STATUS_LEMBRETE_ORDINAL[StatusLembreteEnum.ERRO];
        break;
      default:
        this.filtro.status = undefined;
    }
    this.zerarPaginacao();
  }

  zerarPaginacao() {
    this.page = 0;
    this.buscarLembretes();
  }

  abrirModal(lembrete?: Lembrete, visualizar = false) {
    const mobile = window.innerWidth <= 970;
    const posicao = mobile ? { bottom: '0', left: '0' } : { top: '0', right: '0' };

    this.dialog.open(ModalCadastroLembreteComponent, {
      width: mobile ? '100%' : '450px',
      maxWidth: mobile ? '100vw' : '450px',
      height: mobile ? '92dvh' : '100dvh',
      maxHeight: mobile ? '92dvh' : '100dvh',
      position: posicao,
      data: { lembreteId: lembrete?.id, visualizar },
      panelClass: mobile ? 'slide-modal-baixo' : 'slide-modal',
      disableClose: true
    }).afterClosed().subscribe(res => {
      if (res) {
        this.buscarLembretes();
        this.buscarContagem();
      }
    });
  }

  abrirModalVisualizar(lembrete: Lembrete) {
    this.abrirModal(lembrete, true);
  }

  abrirMenuMobile() {
    this.dialog.open(MenuMobileComponent, {
      width: '85%',
      maxWidth: '320px',
      height: '100dvh',
      maxHeight: '100dvh',
      position: { top: '0', left: '0' },
      panelClass: 'slide-modal-esquerda'
    });
  }
}
